import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  User,
  Search,
  UserCheck,
  UserX
} from 'lucide-react';

export default function OperatorPage() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [operatorToDelete, setOperatorToDelete] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    skills: '',
    is_active: true
  });

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const response = await fetch(`${API}/operators`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setOperators(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des opérateurs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOperators();
  };

  const handleOpenDialog = (operator = null) => {
    if (operator) {
      setEditingOperator(operator);
      setForm({
        name: operator.name,
        code: operator.code,
        email: operator.email || '',
        phone: operator.phone || '',
        skills: operator.skills || '',
        is_active: operator.is_active
      });
    } else {
      setEditingOperator(null);
      setForm({
        name: '',
        code: '',
        email: '',
        phone: '',
        skills: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error('Le nom et le code sont obligatoires');
      return;
    }

    try {
      const url = editingOperator
        ? `${API}/operators/${editingOperator.operator_id}`
        : `${API}/operators`;

      const response = await fetch(url, {
        method: editingOperator ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingOperator ? 'Opérateur modifié' : 'Opérateur créé');
        setDialogOpen(false);
        fetchOperators();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDeleteClick = (operator) => {
    setOperatorToDelete(operator);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!operatorToDelete) return;

    try {
      const response = await fetch(`${API}/operators/${operatorToDelete.operator_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Opérateur supprimé');
        fetchOperators();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setDeleteConfirmOpen(false);
      setOperatorToDelete(null);
    }
  };

  const filteredOperators = operators.filter(op =>
    op.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    op.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (op.email && op.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl uppercase tracking-tight">
            Gestion des Opérateurs
          </h1>
          <p className="text-muted-foreground font-mono text-sm">
            {operators.length} opérateur{operators.length > 1 ? 's' : ''} enregistré{operators.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button onClick={() => handleOpenDialog()} className="gap-2">
            <Plus className="w-4 h-4" />
            Nouvel Opérateur
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Rechercher un opérateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm border-2"
        />
      </div>

      {/* Operators Table */}
      <Card className="border-2">
        <CardHeader className="border-b-2">
          <CardTitle className="font-heading uppercase tracking-tight text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Liste des Opérateurs
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredOperators.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <User className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Aucun opérateur trouvé' : 'Aucun opérateur enregistré'}
              </p>
              {!searchTerm && (
                <Button onClick={() => handleOpenDialog()} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un opérateur
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="font-mono uppercase text-xs">Code</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Nom</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Email</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Téléphone</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Compétences</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Statut</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOperators.map((operator) => (
                  <TableRow key={operator.operator_id} className="border-b">
                    <TableCell className="font-mono font-bold">{operator.code}</TableCell>
                    <TableCell className="font-medium">{operator.name}</TableCell>
                    <TableCell className="text-muted-foreground">{operator.email || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{operator.phone || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {operator.skills || '-'}
                    </TableCell>
                    <TableCell>
                      {operator.is_active ? (
                        <Badge variant="default" className="gap-1 bg-green-600">
                          <UserCheck className="w-3 h-3" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <UserX className="w-3 h-3" />
                          Inactif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(operator)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteClick(operator)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {editingOperator ? 'Modifier l\'opérateur' : 'Nouvel opérateur'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-mono uppercase text-xs">Code *</Label>
                <Input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="OP001"
                  className="rounded-sm border-2"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono uppercase text-xs">Nom *</Label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jean Dupont"
                  className="rounded-sm border-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-mono uppercase text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jean.dupont@example.com"
                  className="rounded-sm border-2"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-mono uppercase text-xs">Téléphone</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  className="rounded-sm border-2"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-mono uppercase text-xs">Compétences</Label>
              <Textarea
                value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                placeholder="Fraisage, Tournage, Soudure..."
                className="rounded-sm border-2 min-h-20"
              />
            </div>

            <div className="flex items-center justify-between p-3 border-2 rounded-sm">
              <div>
                <Label className="font-mono uppercase text-xs">Statut</Label>
                <p className="text-sm text-muted-foreground">
                  {form.is_active ? 'Opérateur actif' : 'Opérateur inactif'}
                </p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave}>
                {editingOperator ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-destructive">
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>

          <p className="text-muted-foreground">
            Êtes-vous sûr de vouloir supprimer l'opérateur{' '}
            <span className="font-semibold text-foreground">
              {operatorToDelete?.name}
            </span>{' '}
            ({operatorToDelete?.code}) ?
          </p>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
